import { useState, useContext, useEffect, useRef } from "react";
import { jwtDecode } from "jwt-decode";
import styled, { keyframes } from "styled-components";
import { useTranslation } from "react-i18next";
import { AuthUserContext } from "../../context/AuthUserContext";
import APIService from "../../services/APIService";
import { useNavigate } from "react-router-dom";
import SimpleContext from "../../context/SimpleContext";

const Container = styled.div`
  width: 100vw;
  position: fixed;
  bottom: 0;
  left: 0;
  background-color: #00000027;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999;
  min-height: 100vh;
`;
const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-direction: column;

  width: 90%;

  background-color: #1a2c38;
  background-color: var(--bg-200);
  position: relative;
  overflow: auto; /* omogućava skrolanje unutar div-a */
  -webkit-overflow-scrolling: touch;

  padding: 40px 0;
  border-radius: 14px;
  @media (min-width: 768px) {
    width: 600px;
    min-height: auto;
  }
`;

const FormContainer = styled.div`
  width: 100%;
  @media (min-width: 768px) {
    //width: 500px;
  }
`;

const Title = styled.h1`
  font-size: var(--font-size-xlarge);
  text-align: center;
  font-weight: 400;
  padding: 0;
  margin: 0;
  color: var(--text-200);
  text-transform: uppercase;
  margin-bottom: var(--spacing-sm);
  padding-bottom: var(--spacing-xs);

  border-bottom: 1px solid var(--bg-300);
`;
const Subtitle = styled.div`
  font-size: var(--font-size-small);
  color: var(--text-100);
  text-align: center;

  margin-bottom: var(--spacing-xl);
  max-width: 80%;
  margin: 0 auto;
`;
const InputGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin: var(--spacing-md) 0 var(--spacing-xl) 0;
`;

const InputBox = styled.input`
  width: 50px;
  height: 50px;
  font-size: 24px;
  text-align: center;
  margin: 0 5px;
  border: 2px solid #dcdcdc;

  &:focus {
    border-color: var(--primary-200);
    outline: none;
  }
`;

const ResendLink = styled.span`
  color: var(--primary-200);
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: var(--primary-200);
  }
`;
const ExitContainer = styled.div`
  display: block;
  position: absolute;
  top: 40px;
  cursor: pointer;
  right: 10%;
`;

const Verification = () => {
  const { t } = useTranslation();
  const inputRefs = useRef([]);
  const [code, setCode] = useState(["", "", "", ""]); // State to track the 4 digits
  const { authTokens, setUserProfile, userProfile, setAuthTokens, logoutUser } =
    useContext(AuthUserContext);
  const [canResend, setCanResend] = useState(true); // Da li je moguće ponovo poslati kod
  const [showConfirm, setShowConfirm] = useState(false);
  const { setShowVerification } = useContext(SimpleContext);
  const [timer, setTimer] = useState(0); // Brojač za ponovno slanje
  const navigate = useNavigate();

  // useEffect za inicijalno slanje koda sa zakašnjenjem od 1 sekunde
  useEffect(() => {
    const lastResendTime = localStorage.getItem("lastResendTime");

    if (lastResendTime) {
      const elapsedTime = (new Date().getTime() - lastResendTime) / 1000; // Razlika u sekundama
      if (elapsedTime < 40) {
        // Ako je prošlo manje od 40 sekundi, pokreni odbrojavanje
        setCanResend(false);
        setTimer(40 - Math.floor(elapsedTime)); // Postavi preostalo vreme u sekunde
      } else {
        // Ako je prošlo više od 40 sekundi, dozvoli ponovno slanje
        setCanResend(true);
      }
    } else {
      // Ako nikada nije poslat kod, pokreni handleResendCode nakon 1 sekunde
      const timeout = setTimeout(() => {
        handleResendCode(); // Prvo slanje koda
      }, 500); // Zakašnjenje od 1 sekunde

      return () => clearTimeout(timeout); // Očisti timeout ako se komponenta demontira pre isteka vremena
    }
  }, []); // Praćenje pri prvom učitavanju stranice
  // useEffect za praćenje vremena i onemogućavanje ponovnog slanja
  useEffect(() => {
    // Provera da li postoji zapis o poslednjem slanju koda u localStorage
    const lastResendTime = localStorage.getItem("lastResendTime");
    if (lastResendTime) {
      const elapsedTime = (new Date().getTime() - lastResendTime) / 1000; // Razlika u sekundama
      if (elapsedTime < 40) {
        setCanResend(false);
        setTimer(40 - Math.floor(elapsedTime)); // Postavi preostalo vreme u sekunde
      }
    }

    // Postavljanje intervala za odbrojavanje
    const interval = setInterval(() => {
      if (timer > 0) {
        setTimer((prevTimer) => prevTimer - 1);
      } else {
        setCanResend(true); // Kada istekne vreme, dozvoli ponovno slanje
        localStorage.removeItem("lastResendTime");
      }
    }, 1000);

    return () => clearInterval(interval); // Očisti interval kada komponenta bude demontirana
  }, [timer]);

  // Function to handle input changes
  const handleInputChange = (e, index) => {
    const value = e.target.value;

    if (/^[0-9]$/.test(value) || value === "") {
      // Allow only single digit
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);

      // Automatically move to the next input
      if (value.length === 1 && index < inputRefs.current.length - 1) {
        inputRefs.current[index + 1].focus();
      }

      // Handle backspace to move focus back
      if (value === "" && index > 0) {
        inputRefs.current[index - 1].focus();
      }

      // Automatically submit if all fields are filled
      if (newCode.every((digit) => digit !== "")) {
        handleSubmit(newCode.join("")); // Submit the concatenated code
      }
    }
  };

  // Function to handle form submission
  const handleSubmit = async (fullCode) => {
    try {
      const response = await APIService.VerifyCode(fullCode, authTokens.access);
      console.log("Verification response:", response);
      // Ažurirajte lokalni userProfile
      if (userProfile) {
        const updatedUserProfile = { ...userProfile, is_email_verified: true };
        setUserProfile(updatedUserProfile);

        // Čuvanje ažuriranog profila u localStorage
        localStorage.setItem(
          "userProfileData",
          JSON.stringify(updatedUserProfile)
        );
      }
      setAuthTokens({ access: response.access, refresh: response.refresh });
      localStorage.setItem(
        "authTokens",
        JSON.stringify({ access: response.access, refresh: response.refresh })
      );
      const decoded = jwtDecode(response.access);
      console.log("Decoded", decoded);
      navigate(-1);
    } catch (error) {
      // Reset polja za unos nakon slanja
      setCode(["", "", "", ""]);
    }
  };

  // Function to handle resend code logic
  const handleResendCode = async () => {
    if (!canResend) return; // Ako nije dozvoljeno slanje, prekini funkciju

    try {
      const decodedToken = jwtDecode(authTokens.access);
      const email = decodedToken.email;
      console.log(email);
      // Poziv API-ja za ponovno slanje koda
      const response = await APIService.ResendVerification(email);

      // Sačuvaj vreme kada je kod poslat
      const currentTime = new Date().getTime();
      localStorage.setItem("lastResendTime", currentTime);

      // Pokreni odbrojavanje za 40 sekundi
      setCanResend(false);
      setTimer(40); // Postavi timer na 40 sekundi
    } catch (error) {
      console.error("Resend error:", error);
    }
  };
  useEffect(() => {
    handleResendCode();
  }, []);

  const decodedToken = jwtDecode(authTokens.access);
  const email = decodedToken.email;
  return (
    <>
      <Container className="header-top-padding">
        <Wrapper
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <ExitContainer
            onClick={(e) => setShowConfirm(true)}
            role="button"
            tabIndex={0}
            aria-label={t("ARIA.CLOSE")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setShowConfirm(true);
              }
            }}
          >
            <svg
              width="24px"
              height="24px"
              viewBox="0 0 1024 1024"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill="var(--text-200)"
                d="M195.2 195.2a64 64 0 0 1 90.496 0L512 421.504 738.304 195.2a64 64 0 0 1 90.496 90.496L602.496 512 828.8 738.304a64 64 0 0 1-90.496 90.496L512 602.496 285.696 828.8a64 64 0 0 1-90.496-90.496L421.504 512 195.2 285.696a64 64 0 0 1 0-90.496z"
              />
            </svg>
          </ExitContainer>
          <FormContainer>
            <Title>{t("VERIFICATION.TITLE")}</Title>
            <Subtitle>{t("VERIFICATION.SUBTITLE", { email })}</Subtitle>
            <InputGroup>
              {Array(4)
                .fill("")
                .map((_, index) => (
                  <InputBox
                    key={index}
                    type="text"
                    maxLength="1"
                    value={code[index]} // Controlled input
                    ref={(el) => (inputRefs.current[index] = el)} // Assign refs to each input
                    onChange={(e) => handleInputChange(e, index)}
                    aria-label={t("ARIA.VERIFICATION_CODE_DIGIT", {
                      number: index + 1,
                    })}
                  />
                ))}
            </InputGroup>
            <Subtitle>
              {t("VERIFICATION.RESEND_INFO")}{" "}
              {canResend ? (
                <ResendLink onClick={handleResendCode}>
                  {t("VERIFICATION.RESEND_LINK")}
                </ResendLink>
              ) : (
                <span>{t("VERIFICATION.TIMER_INFO", { timer })}</span>
              )}
            </Subtitle>
          </FormContainer>
        </Wrapper>
      </Container>
      {showConfirm && (
        <ConfirmContainer>
          <ConfirmWrapper>
            {t("PROFILE.CONFIRM_LOGOUT")}

            <ButtonContainer>
              <ButtonConfirm
                className="button-transparent"
                onClick={() => {
                  setShowVerification(false);
                  logoutUser();
                }}
              >
                {t("BUTTONS.LOGOUT")}
              </ButtonConfirm>
              <ButtonTransparent
                className="button-transparent"
                onClick={(e) => setShowConfirm(false)}
              >
                {t("BUTTONS.CANCEL")}
              </ButtonTransparent>
            </ButtonContainer>
          </ConfirmWrapper>
        </ConfirmContainer>
      )}
    </>
  );
};

export default Verification;

const ConfirmContainer = styled.div`
  margin-top: var(--spacing-xs);
  background-color: #00000031;
  position: fixed;
  left: 0;
  top: 0;
  z-index: 99999999;
  max-width: 100vw;
  width: 100vw;
  height: 100vh;
  box-sizing: border-box;
  overflow-x: hidden;

  display: flex;
  align-items: center;
  justify-content: center;
`;
const ConfirmWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-direction: column;

  background-color: #1a2c38;
  background-color: var(--bg-200);
  position: relative;
  overflow: auto; /* omogućava skrolanje unutar div-a */
  -webkit-overflow-scrolling: touch;

  padding: 60px 0;
  width: 94%;
  border-radius: 8px;
  @media (min-width: 768px) {
    width: 500px;
  }
`;
const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: var(--spacing-sm);
  margin-top: var(--spacing-xl);
`;

const ButtonTransparent = styled.button`
  padding: var(--spacing-sm) var(--spacing-lg);

  cursor: pointer;
  color: var(--primary-200);
  background-color: var(--bg-100);
  &:hover {
    color: var(--primary-100);
    background-color: var(--bg-200);
  }
`;
const ButtonConfirm = styled(ButtonTransparent)`
  color: var(--error-color);
  background-color: var(--bg-100);
  border-color: var(--error-color);
  &:hover {
    color: var(--bg-100);
    background-color: var(--error-color);
  }
`;
